import { NgSignaturePadOptions, SignaturePadComponent } from '@almothafar/angular-signature-pad';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Company } from 'src/app/models/company.model';
import { SignatureRequestService } from 'src/app/services/signature-request.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PopupProgressSigningComponent } from './popup-progress-signing/popup-progress-signing.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Document } from 'src/app/models/document.model';
import { AuthService } from 'src/app/services/auth.service';
import { SignaturesHeaderService } from 'src/app/services/signatures-header.service';

@Component({
  selector: 'app-document-signer',
  templateUrl: './document-signer.component.html',
  styleUrls: ['./document-signer.component.sass']
})
export class DocumentSignerComponent implements OnInit, AfterViewInit {

  @ViewChild('signature')
  public signaturePad: SignaturePadComponent = null as any;

  public signaturePadOptions: NgSignaturePadOptions = { // passed through to szimek/signature_pad constructor
    canvasWidth: 0,
    canvasHeight: 0,
    minDistance: 1,
    minWidth: 0.5,
    maxWidth: 2.5
  };

  control:"draw"|"erase" = "draw";

  document:Document = null as any;
  public requireDni:boolean = false;
  public dni:string = '';
  public step:'dni'|'sign' = 'sign';

  private _strokeWidth:number = 5;
  get strokeWidth(): number { return this._strokeWidth; }
  set strokeWidth(n:number) {
    this._strokeWidth = n;
    this.setStrokeWidth(n);
  }

  constructor(private signature_request:SignatureRequestService,
              private utils:UtilsService,
              private dialog:MatDialog,
              private router:Router,
              private activated_route:ActivatedRoute,
              private auth:AuthService,
              private header:SignaturesHeaderService) {

  }

  ngOnInit(): void {
    this.checkIfDocumentIsSet();
    this.listenSignatureRequest();
  }

  ngAfterViewInit(): void {
    this.resizeCanvas();
  }

  /**
   * Avança al pas de firma un cop introduït el DNI.
   */
  goToSignStep() {
    if(!this.dni.trim()) {
      return;
    }
    this.step = 'sign';
    // El signature-pad s'acaba de renderitzar; cal dimensionar el canvas un cop al DOM.
    setTimeout(() => this.resizeCanvas());
  }

  private resizeCanvas() {
    if(this.signaturePad == null) {
      return;
    }
    this.signaturePad.set('canvasWidth', document.querySelector('signature-pad')?.clientWidth); // set szimek/signature_pad options at runtime
    this.signaturePad.set('canvasHeight', document.querySelector('signature-pad')?.clientHeight); // set szimek/signature_pad options at runtime
  }

  erase() {
    this.signaturePad.clear();
  }

  sign() {
    if(this.requireDni && !this.dni.trim()) {
      return;
    }

    const type = 'image/png';
    const canvas = this.signaturePad.getCanvas();
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const signatureBounds = this.calculateSignatureBounds(pixels, canvas.width, canvas.height);

    if (signatureBounds) {
      const signatureImageData = context.getImageData(
        signatureBounds.minX,
        signatureBounds.minY,
        signatureBounds.maxX - signatureBounds.minX,
        signatureBounds.maxY - signatureBounds.minY
      );

      // Aquí puedes realizar manipulaciones adicionales en signatureImageData si es necesario

      const signatureCanvas = document.createElement('canvas');
      signatureCanvas.width = signatureBounds.maxX - signatureBounds.minX;
      signatureCanvas.height = signatureBounds.maxY - signatureBounds.minY;
      const signatureContext = signatureCanvas.getContext('2d') as CanvasRenderingContext2D;
      signatureContext.putImageData(signatureImageData, 0, 0);
      const signature = signatureCanvas.toDataURL(type);
      const signature_blob: Blob = this.base64ToBlob(signature);
      this.openSignPopup(signature_blob);

    } else {

    }
  }

  calculateSignatureBounds(pixels: Uint8ClampedArray, width: number, height: number): SignatureBounds | null {
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let foundPixel = false;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = pixels[(y * width + x) * 4 + 3];
        if (alpha !== 0) {
          foundPixel = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (foundPixel) {
      return {
        minX,
        minY,
        maxX: maxX + 1,
        maxY: maxY + 1
      };
    }

    return null;
  }

  private listenSignatureRequest() {
    this.signature_request.company.subscribe(
      data => {
        if(data != null) {

          // setejem color
          if(data.color != null) {
            this.setSliderColors(data);
          }
        }
      }
    );

    this.signature_request.signature_request.subscribe(
      data => {
        if(data != null) {
          this.requireDni = !!data.require_dni;
          this.step = this.requireDni ? 'dni' : 'sign';
        }
      }
    );
  }

  private setSliderColors(data:Company) {
    const item:HTMLElement = document.querySelector('.mat-mdc-slider.mat-primary') as HTMLElement;
    if(item!=null) {
      item.style.setProperty("--mdc-slider-handle-color", data.color);
      item.style.setProperty("--mdc-slider-focus-handle-color", data.color);
      item.style.setProperty("--mdc-slider-hover-handle-color", data.color);
      item.style.setProperty("--mdc-slider-active-track-color", data.color);
      item.style.setProperty("--mdc-slider-inactive-track-color", data.color);
      item.style.setProperty("--mdc-slider-with-tick-marks-active-container-color", data.text_color==="light" ? "#000" : "#fff");
      item.style.setProperty("--mdc-slider-with-tick-marks-inactive-container-color", data.color);
      item.style.setProperty("--mat-mdc-slider-ripple-color", data.color);
      item.style.setProperty("--mat-mdc-slider-hover-ripple-color", "rgba(79, 167, 112, 0.5)");
      item.style.setProperty("--mat-mdc-slider-focus-ripple-color: rgba(79, 167, 112, ", "2)");
    }
  }

  /**
   * Si n = 5, output serà 0.5 i 2.5 (min / max)
   * @param n from 1 to 10
   */
  private setStrokeWidth(n:number) {
    this.signaturePad.set('minWidth', 0.5 * n / 5);
    this.signaturePad.set('maxWidth', 2.5 * n / 5);
  }

  private base64ToBlob(dataURI:string): Blob {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does
    var byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    //Old Code
    //write the ArrayBuffer to a blob, and you're done
    //var bb = new BlobBuilder();
    //bb.append(ab);
    //return bb.getBlob(mimeString);

    //New Code
    return new Blob([ab], {type: mimeString});
  }

  private openSignPopup(signature:Blob) {
    const dialogRef = this.dialog.open(PopupProgressSigningComponent, {
      disableClose: true,
      width: '90%',
      maxWidth: '400px',
      data: {
        signature_request: this.signature_request.getSignatureRequest(),
        signature: signature,
        dni: this.requireDni ? this.dni.trim() : null
      }
    });

    dialogRef.afterClosed().subscribe(
      (data:{logout:boolean}) => {
        if(data.logout) {
          this.closeAndLogout();
        }
      }
    );
  }

  private checkIfDocumentIsSet() {
    this.document = this.signature_request.getDocument();
    if(this.document==null) {
      this.router.navigate(['view'], { relativeTo: this.activated_route });
    }
  }

  private closeAndLogout() {
    this.auth.logout();
    this.signature_request.reset();
    this.header.reset();
    this.router.navigate(['/']);
  }


}

interface SignatureBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}
